import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import type { ClinicBranding } from "@dentacore/shared-types";

import { readClinicBranding } from "../api/clinic";
import { useAuth } from "../auth/auth-context";
import { useCan, useCanAny } from "../auth/use-can";
import { AccountMenu } from "./account-menu";
import { BranchSwitcher } from "./branch-switcher";
import { QuickRing } from "./quick-ring";
import { ModulesProvider, useModules } from "./modules-context";
import { isDivider, isGroup, NAV } from "./nav-items";
import { useWaitingRequests } from "./use-waiting-requests";
import { CartProvider, useCart } from "../supply/cart";
import { useActiveBranch } from "../api/use-active-branch";
import type { NavEntry, NavItem } from "./nav-items";
import { SubscriptionBanner } from "./subscription-banner";
import { WaitingChip } from "./waiting-chip";
import { WaitingProvider } from "./waiting-context";
import { LowStockWatchProvider } from "../inventory/low-stock-watch";
import { SetupBanner } from "../setup/setup-banner";
import { WelcomeDialog } from "../setup/welcome-dialog";
import styles from "./shell.module.css";
import { useOpenTasks } from "../tasks/use-open-tasks";
import { startLiveUpdates } from "../api/live";
import { HelpPanel } from "../help/help-panel";

/**
 * The frame every screen sits inside.
 *
 * Three things here are not in the mockup, and each is deliberate:
 *
 *   - **No macOS traffic lights.** The mockup is drawn as a desktop window;
 *     this is a web app. CLAUDE.md: keep the density, drop the chrome.
 *   - **A branch switcher**, because the mockup predates multi-branch entirely
 *     and shows a hardcoded "Cedar Dental · Branch A" where the clinic's real
 *     name and the user's real branches belong.
 *   - **A demo banner**, because the mockup has no notion of seeded data and
 *     this application does.
 *
 * Everything else is measured from it — 44px banner, 212px sidebar, 60px
 * collapsed, and the same five destinations in the same order. **A sixth,
 * Settings, follows them for admins only** (decision S2/S3); a clinician still
 * sees exactly the mockup's five.
 */
export function AppShell(): React.JSX.Element {
  // One live stream per signed-in tab (OQ-D19); screens refetch from it.
  useEffect(() => startLiveUpdates(), []);
  return (
    <ModulesProvider>
      <CartScope>
        <Shell />
      </CartScope>
    </ModulesProvider>
  );
}

/** The one cart, held by the shell so the sidebar can count it (MS-D4). */
function CartScope({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { user } = useAuth();
  const branchId = useActiveBranch();
  const { states, writable } = useModules();
  const can = useCan();
  // Only once the modules are known: before that, asking would be a guess.
  const enabled = states !== null && writable("SUPPLY") && can("inventory.view");
  return (
    <CartProvider userId={user?.id ?? null} branchId={branchId} enabled={enabled}>
      {children}
    </CartProvider>
  );
}

function Shell(): React.JSX.Element {
  const { user } = useAuth();
  const { has, locked: isLocked, readOnly, hasPreview } = useModules();
  const canAny = useCanAny();
  // **A tablet starts with the sidebar as icons** (OQ-D32), giving the chart and
  // the calendar the width; it opens like anywhere else.
  const [collapsed, setCollapsed] = useState(
    () => document.documentElement.dataset.touch === "true",
  );

  /**
   * The sidebar as a drawer, below 900px — **and nothing else changes.**
   *
   * Measured before this was written: every route spilled **179px at 375px**,
   * and 212px of a 375px screen is 57% of the window spent on navigation.
   *
   * **A drawer rather than an icon rail**, which was the cheaper option and the
   * wrong one: the rail has room for the seven icons and nothing else, so the
   * clinic name, the **branch switcher** and sign-out would all have had to go
   * somewhere. The switcher decides whose data you are reading; it does not get
   * quietly dropped on the device most likely to be used at the chair.
   *
   * **Its own state, separate from `collapsed`.** Collapsing is a preference
   * somebody expressed on a wide screen and should survive a narrow one; the
   * drawer is a thing that is open or shut right now. Mixing them would have a
   * phone inherit a decision made on a laptop.
   */
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  /**
   * The banner's search term (Anthony's QA, item 4).
   *
   * Local to the banner and **not** synced back from the URL: this is a place
   * to start a search from, and the patients screen's own box is the one that
   * holds the search you are in the middle of. Two inputs writing to each other
   * is how a search box fights the person typing in it.
   */
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  // The help panel beside the page (help-panel grill, 2026-11-02).
  const [helpOpen, setHelpOpen] = useState(false);

  // Going somewhere is the end of choosing where to go.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  /**
   * **⌘K focuses the box** — the hint beside it was as decorative as the box.
   *
   * `metaKey || ctrlKey` so the same label works on both platforms without
   * detecting one; the glyph stays ⌘ because that is what the mockup draws.
   */
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== "k" || !(event.metaKey || event.ctrlKey)) return;
      event.preventDefault();
      searchRef.current?.focus();
      searchRef.current?.select();
    }

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  /**
   * Escape closes it, and focus goes back to the control that opened it.
   *
   * Without the second half, dismissing the drawer leaves the focus ring on an
   * element that is now off-canvas — the next Tab starts from nowhere, which
   * for a keyboard user is the screen having silently moved.
   */
  useEffect(() => {
    if (!menuOpen) return;

    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      setMenuOpen(false);
      menuButton.current?.focus();
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  // Each link shows to whoever holds a permission it needs (STORY-166). This
  // is presentation, not access control — `RequirePermission` bounces the
  // route and the API refuses on its own, whatever this list says (S3).
  // The waiting count the old Time off tab carried, on HR → Requests (MS-D3).
  const waitingRequests = useWaitingRequests();
  // The cart's line count, on Orders → Cart — the old Cart tab's "(3)".
  const cartLines = useCart().lines.length;
  // Open tasks the reader can act on, at the locations shown (TK-D38).
  const openTasks = useOpenTasks();

  const shows = (item: NavItem, groupModule?: NavItem["module"]): boolean => {
    if (item.needs !== undefined && !canAny(item.needs)) return false;
    if (item.hideFor !== undefined && canAny([item.hideFor])) return false;
    // An add-on's page inside a base group is absent without it (MS-D5).
    const module = item.module ?? groupModule;
    if (item.hideWhenLocked === true && module !== undefined && !has(module)) return false;
    // A preview is absent, not locked, without it (TK-D36).
    if (item.preview !== undefined && !hasPreview(item.preview)) return false;
    return true;
  };

  /**
   * The sidebar as drawn: groups that have no link left for this person are
   * dropped, and so is a divider with nothing after it or two in a row.
   */
  const visibleNav: NavEntry[] = [];
  for (const entry of NAV) {
    if (isDivider(entry)) {
      const last = visibleNav[visibleNav.length - 1];
      if (last !== undefined && !isDivider(last)) visibleNav.push(entry);
      continue;
    }
    if (isGroup(entry)) {
      const items = entry.items.filter((item) => shows(item, entry.module));
      if (items.length > 0) visibleNav.push({ ...entry, items });
      continue;
    }
    if (shows(entry)) visibleNav.push(entry);
  }
  while (visibleNav.length > 0 && isDivider(visibleNav[visibleNav.length - 1] as NavEntry)) {
    visibleNav.pop();
  }

  /**
   * **Which groups are open** (MS-D7, recut after Emile's comment on the
   * prototype: *"extremely ugly"*). A group is a row like any other — its icon,
   * its name and a chevron — and its pages fold under it on a thin guide line,
   * without icons of their own. The group holding the page on screen opens by
   * itself; any other opens when clicked, and stays as it was left.
   */
  const [opened, setOpened] = useState<Record<string, boolean>>({});
  const groupHolds = (items: readonly NavItem[]): boolean =>
    items.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
  const iconOnly = collapsed && !menuOpen;

  /** One link, locked or not — a top-level item or a group's page alike. */
  const link = (item: NavItem, groupModule?: NavItem["module"], nested = false) => {
    const module = item.module ?? groupModule;
    // Drawn locked only once known: unknown is neither locked nor had (QF-D9).
    const locked = module !== undefined && isLocked(module);
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end === true}
        className={nested ? `${styles.navItem ?? ""} ${styles.navSub ?? ""}` : styles.navItem}
        title={locked ? `${item.label} is an add-on your clinic does not have` : item.label}
        data-locked={locked ? "true" : undefined}
        data-testid={item.testId}
      >
        {/* A group's pages carry no icon: the group's own says what they are. */}
        {!nested && (
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            style={{ flex: "none" }}
          >
            <path d={item.icon} />
          </svg>
        )}
        <span className={styles.navLabel}>{item.label}</span>
        {item.testId === "nav-orders-cart" && cartLines > 0 && (
          <span className={styles.navCount} data-testid="cart-count">
            {cartLines}
          </span>
        )}
        {item.testId === "nav-tasks" && openTasks !== null && openTasks > 0 && (
          <span className={styles.navCount} data-testid="tasks-open-count">
            {openTasks}
          </span>
        )}
        {item.testId === "nav-hr-requests" && waitingRequests !== null && waitingRequests > 0 && (
          <span className={styles.navCount} data-testid="time-off-waiting-count">
            {waitingRequests}
          </span>
        )}
        {/*
          A locked module stays in the sidebar (MOD-D11, MS-D5). Marked with a
          word rather than faded: the label keeps its full contrast. A group says
          it once, on its heading, rather than on every link under it.
        */}
        {item.module !== undefined && (isLocked(item.module) || readOnly(item.module)) && (
          <span className={styles.navTag}>{readOnly(item.module) ? "Read only" : "Add-on"}</span>
        )}
      </NavLink>
    );
  };

  /**
   * The clinic's logo (STORY-070).
   *
   * **Fetched rather than read from the token**, because the URL is signed and
   * short-lived: baking it into the stored `AuthUser` would put an expired
   * address into every reloaded tab. One call on mount, and the browser has the
   * image well inside the URL's life.
   *
   * **Failure is silent and renders nothing.** A logo that could not be reached
   * must not put an error on a dentist's screen — the clinic name is already
   * there and says which clinic this is.
   */
  const [branding, setBranding] = useState<ClinicBranding | null>(null);

  useEffect(() => {
    if (user === null) return;

    const controller = new AbortController();
    readClinicBranding(controller.signal)
      .then(setBranding)
      .catch(() => {
        /* The name alone is a complete identity. */
      });

    return () => controller.abort();
  }, [user]);

  return (
    /*
      **The provider wraps the whole shell, not just the header** (STORY-057).
      The chip reads the count and the calendar's queue refreshes it, so the two
      have to share one state — and the queue is rendered through `<Outlet />`
      below. `WaitingChip` is its own component for the same reason: a component
      cannot consume a context it renders itself.
    */
    <WaitingProvider>
      <LowStockWatchProvider>
        <div className={styles.app}>
          <div className={styles.body}>
            {/*
            **The scrim, and it is a button.** Tapping beside a drawer to shut
            it is the gesture people arrive with, and a `<div>` with an onClick
            answers a pointer and nothing else. It carries no label because the
            Escape handler and the toggle above are the accessible routes out —
            this is the pointer's shortcut, hidden from the reading order.
          */}
            {menuOpen && (
              <button
                type="button"
                className={styles.scrim}
                tabIndex={-1}
                aria-hidden="true"
                data-testid="menu-scrim"
                onClick={() => {
                  setMenuOpen(false);
                }}
              />
            )}

            <nav
              id="app-sidebar"
              className={styles.sidebar}
              data-collapsed={collapsed}
              data-open={menuOpen}
              aria-label="Sections"
              data-testid="sidebar"
            >
              <div className={styles.brand}>
                <span className={styles.brandMark} aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 3.5c-2.6 0-4.3 2.1-4.3 4.8 0 3.1 1.3 4.7 1.9 7.8.4 2.1 1 4.4 2.4 4.4 1.6 0 1.6-4.5 4-4.5s2.4 4.5 4 4.5c1.4 0 2-2.3 2.4-4.4.6-3.1 1.9-4.7 1.9-7.8 0-2.7-1.7-4.8-4.3-4.8-1.7 0-2.6.9-4 .9S9.7 3.5 8 3.5z" />
                  </svg>
                </span>
                {!iconOnly && <span className={styles.brandName}>DentaCore</span>}
              </div>
              <div className={styles.sidebarHead}>
                <button
                  type="button"
                  className={styles.collapse}
                  onClick={() => setCollapsed((was) => !was)}
                  aria-expanded={!collapsed}
                  aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                >
                  ☰
                </button>

                {/*
                **Or the drawer is open**, which is the case this clause used to
                miss. `collapsed` is a preference somebody set on a wide screen;
                carried onto a phone unchecked, it would open a drawer with no
                clinic name and no branch switcher in it — the two things the
                drawer exists to keep reachable.
              */}
                {(!collapsed || menuOpen) && (
                  <div className={styles.identity}>
                    {/*
                  The clinic's own name, from the database. Never a constant.

                  **The logo joins the name rather than replacing it** (decision
                  L4): a name is readable at any size, survives a logo that fails
                  to load, and is what a screen reader has to work with. So the
                  logo is decorative here and carries no alt text — the name
                  beside it already says which clinic this is.
                */}
                    <div className={styles.clinicLine}>
                      {branding?.logoUrl != null && (
                        <img
                          className={styles.clinicLogo}
                          src={branding.logoUrl}
                          alt=""
                          data-testid="sidebar-logo"
                        />
                      )}
                      <div className={styles.clinicName}>{user?.clinicName}</div>
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.rule} />

              <div className={styles.nav}>
                {visibleNav.map((entry, index) => {
                  if (isDivider(entry)) {
                    return <div key={`divider-${String(index)}`} className={styles.navDivider} />;
                  }
                  if (!isGroup(entry)) return link(entry);
                  const holds = groupHolds(entry.items);
                  const open = opened[entry.label] ?? holds;
                  const first = entry.items[0];
                  /*
                    **Icon only, the group is one link** to its first page —
                    there is no room to fold anything under it.
                  */
                  if (iconOnly && first !== undefined) {
                    return (
                      <NavLink
                        key={entry.label}
                        to={first.to}
                        className={styles.navItem}
                        aria-current={holds ? "page" : undefined}
                        title={entry.label}
                        data-testid={entry.testId}
                      >
                        <svg
                          width="17"
                          height="17"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                          style={{ flex: "none" }}
                        >
                          <path d={entry.icon} />
                        </svg>
                        <span className={styles.navLabel}>{entry.label}</span>
                      </NavLink>
                    );
                  }
                  return (
                    <div key={entry.label} className={styles.navGroup} data-testid={entry.testId}>
                      <button
                        type="button"
                        className={styles.navItem}
                        aria-expanded={open}
                        data-holds={holds ? "true" : undefined}
                        onClick={() => setOpened((was) => ({ ...was, [entry.label]: !open }))}
                        data-testid={`${entry.testId}-toggle`}
                      >
                        <svg
                          width="17"
                          height="17"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                          style={{ flex: "none" }}
                        >
                          <path d={entry.icon} />
                        </svg>
                        <span className={styles.navLabel}>{entry.label}</span>
                        {entry.module !== undefined &&
                          (isLocked(entry.module) || readOnly(entry.module)) && (
                            <span className={styles.navTag}>
                              {readOnly(entry.module) ? "Read only" : "Add-on"}
                            </span>
                          )}
                        <span className={styles.navChevron} aria-hidden="true" />
                      </button>
                      {open && (
                        <div className={styles.navChildren} role="group" aria-label={entry.label}>
                          {entry.items.map((item) => link(item, entry.module, true))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </nav>

            <div className={styles.panel}>
              <header className={styles.banner}>
                {/*
            **Only below 900px**, where the sidebar is a drawer. Above it the
            sidebar is always on screen and its own collapse control is the
            right one — two buttons doing the same job on a wide screen is how
            somebody learns to distrust both.
          */}
                <button
                  ref={menuButton}
                  type="button"
                  className={styles.menu}
                  aria-expanded={menuOpen}
                  aria-controls="app-sidebar"
                  aria-label={menuOpen ? "Close the menu" : "Open the menu"}
                  data-testid="menu-toggle"
                  onClick={() => {
                    setMenuOpen((was) => !was);
                  }}
                >
                  ☰
                </button>

                <div className={styles.wordmark}>DentaCore</div>

                {/*
            **The location, beside the name of the product** (decision CS9,
            reversing 2026-09-22's move of it to the sidebar foot). Up here it
            names whose data is on screen, on every screen, and it stays
            reachable with the sidebar collapsed — the foot never was.
          */}
                <BranchSwitcher />
                <div className={styles.spacer} />

                {/*
            **A form, because this control does something now** (Anthony's QA,
            item 4). It shipped as a bare `<input>` with no state and no
            handler — a search box that swallowed every keystroke and answered
            nothing, which is worse than having none at all. Enter takes the
            term to the patients screen, which owns searching and already
            debounces, pages and ranks; duplicating any of that up here would
            give the product two searches that disagree.
          */}
                <form
                  className={styles.search}
                  role="search"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const term = search.trim();
                    if (term === "") return;
                    navigate(`/patients?q=${encodeURIComponent(term)}`);
                  }}
                >
                  <input
                    ref={searchRef}
                    type="search"
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                    }}
                    placeholder="Search patients…"
                    aria-label="Search patients"
                    data-testid="global-search"
                  />
                  <span className={styles.shortcut} aria-hidden="true">
                    ⌘K
                  </span>
                </form>

                <WaitingChip />

                {/*
              **Patients and the cart from anywhere** (Emile, 2026-10-22:
              "make sure the shopping cart is accessible from anywhere just like the
              waiting on WhatsApp button … same for patients (use icons)").
            */}
                {canAny(["patients.view"]) && (
                  <NavLink
                    to="/patients"
                    className={styles.iconLink}
                    aria-label="Patients"
                    title="Patients"
                    data-testid="topbar-patients"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6M3.5 19c0-3 2.4-5 5.5-5s5.5 2 5.5 5M16 11a2.5 2.5 0 1 0 0-5M17.5 14c1.9.4 3 1.9 3 4" />
                    </svg>
                  </NavLink>
                )}
                {has("SUPPLY") && canAny(["inventory.view"]) && (
                  <NavLink
                    to="/orders/cart"
                    className={styles.iconLink}
                    aria-label={
                      cartLines === 0
                        ? "Cart, empty"
                        : `Cart, ${String(cartLines)} ${cartLines === 1 ? "line" : "lines"}`
                    }
                    title="Cart"
                    data-testid="topbar-cart"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M3 4h2.5l2 11h11l2-8H7M9.5 19.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2M17 19.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2" />
                    </svg>
                    {cartLines > 0 && <span className={styles.iconCount}>{cartLines}</span>}
                  </NavLink>
                )}

                {/* Help, for everyone, on every screen (help-panel grill, 2026-11-02). */}
                <button
                  type="button"
                  className={styles.helpButton}
                  aria-label={helpOpen ? "Close help" : "Help"}
                  aria-expanded={helpOpen}
                  aria-pressed={helpOpen}
                  title="Help"
                  data-testid="topbar-help"
                  onClick={() => {
                    setHelpOpen((was) => !was);
                  }}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18M9.6 9.3a2.5 2.5 0 0 1 4.8.9c0 1.7-2.4 2.2-2.4 3.8M12 17h.01" />
                  </svg>
                </button>

                {/* Who is signed in, the theme and Sign out (CS9). */}
                <AccountMenu />
              </header>

              <SubscriptionBanner />

              {user?.isDemoClinic === true && (
                <div className={styles.demo} role="status" data-testid="demo-banner">
                  <span className={styles.demoBadge}>Demo data</span>
                  <span>
                    Everything below belongs to a seeded demo clinic. Remove it with{" "}
                    <code>pnpm db:purge-demo</code>.
                  </span>
                </div>
              )}

              {/*
          **Setup follows you until it is finished** (2026-09-05).

          It used to live on Home alone, which is exactly where a new admin is
          not: the checklist's own steps deep-link into Settings, so the first
          thing anybody does is leave Home — and the way back disappeared with
          it. The client's words: *"the steps to complete the sign up form are
          gone after i click on one of the 4 things to fill in."*

          In the shell it is on every screen, so the work and the way back are
          never on different pages. It still renders nothing for a non-admin, a
          finished clinic, or a failed check (G3, G4), and `/setup` itself is
          excluded because a banner pointing at the page you are reading is
          noise.
        */}
              <SetupBanner />

              {/*
          **The one-off introduction**, over whatever a new admin lands on.

          In the shell rather than on a route, because it is about the clinic
          and not about a screen — and because it has to appear wherever they
          land, which for a brand-new admin is `/setup`. It explains what that
          checklist is *for*, which the checklist itself cannot without becoming
          an essay. Shown once per person per browser, never for a clinic that is
          already set up, and never at all if the check fails; the banner above
          is what keeps nagging, undismissably, exactly as G4 built it.
        */}
              <WelcomeDialog />

              <div className={styles.work}>
                <main className={styles.content}>
                  <Outlet />
                </main>
                {helpOpen && (
                  <HelpPanel
                    onClose={() => {
                      setHelpOpen(false);
                    }}
                  />
                )}
              </div>
            </div>

            <QuickRing />
          </div>
        </div>
      </LowStockWatchProvider>
    </WaitingProvider>
  );
}
